import React, { useState } from 'react';
import { ChartResolution, isChange } from '../utils/chordRespell';
import { ChordSegment } from '../utils/chordChartExport';

/**
 * Review dialog for a batch chord operation (respell for iRealPro, respell by
 * feeling, respell by legibility).
 *
 * Shows every proposed change with a per-chord checkbox, plus a confirm and a
 * dismiss button. Nothing is applied until the user confirms — `onApply` receives
 * exactly the resolutions that were left checked, which is the shape
 * applyResolutions() expects.
 *
 * Styling deliberately matches the Export Chart dropdown (#1a1a1a panel, faint
 * white borders, Trebuchet MS) so batch operations read as part of the same
 * chart-controls family.
 */

export type ChordBatchReviewProps = {
  /** Dialog heading, e.g. "Respell chords by legibility". */
  title: string;
  /**
   * One line of context under the title, e.g. "3 of 14 chords would change".
   * Callers build this because only they know the total chord count.
   */
  summary: string;
  /**
   * Rows to display. Pass every resolution that is not 'unchanged' — both the
   * ones with a substitute and the 'fallback' ones without.
   *
   * Fallback rows are listed but not selectable: they show the chord and
   * "none found". Showing only the fixable chords would misreport how many
   * chords iRealPro cannot render.
   */
  changes: ChartResolution[];
  /**
   * Set when some chords could not be checked (lookupFailures > 0). Shown as a
   * warning so the user knows the list is incomplete rather than assuming
   * every other chord was fine.
   */
  warning?: string;
  /** Label for the confirm button. Defaults to "Change Selected Chords". */
  applyLabel?: string;
  /** Label for the secondary button. Defaults to "Cancel". */
  keepLabel?: string;
  /**
   * Panel width. 'wide' suits the export dialog, whose buttons carry longer
   * labels ("Change Selections & Export"); 'narrow' suits the in-chart respell,
   * whose rows and buttons are shorter.
   */
  width?: 'wide' | 'narrow';
  /** Apply the checked changes. */
  onApply: (accepted: ChartResolution[]) => void;
  /**
   * Take no changes, but continue whatever the operation was.
   *
   * Distinct from onDismiss because the two differ during export:
   * "Keep Originals & Export" exports the chart with Harmona's own spellings,
   * while dismissing the dialog abandons the export entirely. For an in-chart
   * respell both mean "Cancel" — just pass the same handler.
   */
  onKeepAll: () => void;
  /** Backdrop click — abandon the operation. */
  onDismiss: () => void;
  /**
   * Edit the chord at this chart index by hand, from a row with no substitute.
   * The caller closes the dialog, abandoning the operation as a dismiss does.
   */
  onEdit: (index: number) => void;
  /**
   * Renders a segment as a chord symbol.
   *
   * Supplied by the caller (App passes assembleChordSymbol bound to the locked
   * key) so the dialog shows chords with exactly the styling and formatting
   * used in the chord chart and the Chord Symbol panel — superscripts, ∆, ♭/♯
   * and all. Showing raw iRealPro suffix text here would be unreadable to a
   * player and would not match anything else on screen.
   */
  renderChord: (segment: ChordSegment) => React.ReactNode;
  /**
   * Viewport coordinates of the button that opened the dialog, so it appears
   * directly below it like the button's dropdown menu does.
   *
   * Only `bottom` and `right` are used: the panel's RIGHT edge is pinned to the
   * button's right edge and the panel extends leftward from there, because
   * these buttons sit at the right of the chord chart and the panel is much
   * wider than they are — the same reason the dropdown menus offset left.
   *
   * Omit to fall back to a top-anchored, horizontally centred panel.
   */
  anchor?: { bottom: number; right: number } | null;
};

/**
 * Width of the leading slot holding the checkbox (or, on rows with no
 * substitute, nothing). Both row types use it so their chords line up in one
 * column.
 */
const SELECT_SLOT = '0.9dvw';

/**
 * Chord symbols render larger than the surrounding UI text — they're the
 * content — and in the chart's own font.
 *
 * 'Bravura Text' at weight 100 is what .chord-chart uses (App.css:884). The
 * dialog's panel font is Trebuchet MS, so without this the chord symbols came
 * out in the UI font and looked nothing like the chart or the Chord Symbol
 * panel. Keep these in step with .chord-chart if that ever changes.
 */
const CHORD_SIZE = '1.35dvw';
const chordStyle: React.CSSProperties = {
  fontFamily: "'Bravura Text'",
  fontWeight: 100,
  fontSize: CHORD_SIZE,
};

/**
 * The "→" between the two chords. Sized off CHORD_SIZE so it stays in
 * proportion whenever the chord size is tuned, rather than being left behind at
 * the smaller UI text size.
 */
const arrowStyle: React.CSSProperties = {
  fontSize: CHORD_SIZE,
  opacity: 0.5,
};

/** Rows with no substitute are faded to set them apart from the ones that can change. */
const FALLBACK_OPACITY = 0.55;

// UI text sizes, a step up from the original values so the dialog does not read
// small against the rest of the app.
const TITLE_SIZE = '1.05dvw';
const TEXT_SIZE = '0.9dvw';

/**
 * Checkbox sizing must be set explicitly: App.css:1532 styles ALL inputs with
 * `width: 10dvw; height: 1dvw`, which gives a bare checkbox a 10dvw-wide box
 * and pushes everything after it far to the right.
 */
const checkboxStyle: React.CSSProperties = {
  width: SELECT_SLOT,
  height: SELECT_SLOT,
  minWidth: SELECT_SLOT,
  margin: 0,
  padding: 0,
  flex: 'none',
  cursor: 'pointer',
};

/**
 * Dialog buttons, matching the app's chart-control buttons: #aaa background
 * with black text (not white — the rest of the app's buttons are black-on-grey,
 * and the disabled treatment follows .redo-disabled's #606060).
 */
function buttonStyle(enabled: boolean): React.CSSProperties {
  return {
    position: 'static',
    width: 'auto',
    height: 'auto',
    padding: '0.3dvw 0.8dvw',
    fontSize: TEXT_SIZE,
    fontFamily: 'Trebuchet MS',
    fontWeight: 700,
    backgroundColor: enabled ? '#aaa' : '#606060',
    color: 'black',
    border: '0.05dvw solid rgba(0,0,0,0.4)',
    borderRadius: '0.3dvw',
    cursor: enabled ? 'pointer' : 'not-allowed',
  };
}

const ChordBatchReview: React.FC<ChordBatchReviewProps> = ({
  title,
  summary,
  changes,
  warning,
  applyLabel = 'Change Selected Chords',
  keepLabel = 'Cancel',
  onApply,
  onKeepAll,
  onDismiss,
  onEdit,
  renderChord,
  anchor,
  width = 'wide',
}) => {
  // Pin the panel's right edge to the button's right edge so it opens leftward,
  // matching the dropdown menus. Without an anchor, centre it near the top.
  const placement: React.CSSProperties = anchor
    ? { top: anchor.bottom + 4, right: Math.max(4, window.innerWidth - anchor.right) }
    : { top: '9dvh', left: '50%', transform: 'translateX(-50%)' };
  // Every row that gets listed, in chart order: fixable chords and chords with
  // no substitute alike.
  const rows = changes.filter(c => c.resolution.kind !== 'unchanged');
  // Only the fixable ones can be checked or applied.
  const actionable = rows.filter(c => isChange(c.resolution));

  // Indices the user has unchecked. Everything starts accepted, so confirming
  // is one click and per-chord opt-outs are the exception.
  const [rejected, setRejected] = useState<number[]>([]);

  const toggle = (index: number) => {
    setRejected(prev => prev.indexOf(index) >= 0
      ? prev.filter(i => i !== index)
      : [...prev, index]);
  };

  const accepted = actionable.filter(c => rejected.indexOf(c.index) < 0);

  return (
    <>
      {/* Backdrop — click to dismiss, matching the export menu's behaviour. */}
      <div
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1999 }}
        onClick={onDismiss}
      />
      <div style={{
        position: 'fixed',
        // Opens below its button (see `placement`), never vertically centred, so
        // it does not drift as the chord count changes. It grows with its
        // content, but maxHeight keeps the bottom clear of the keyboard display;
        // past that the list scrolls (see the row list below).
        ...placement,
        maxHeight: '56dvh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#1a1a1a',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '0.3dvw',
        boxShadow: '0 0.2dvw 0.5dvw rgba(0, 0, 0, 0.3)',
        zIndex: 2000,
        minWidth: width === 'narrow' ? '15dvw' : '22dvw',
        maxWidth: width === 'narrow' ? '28dvw' : '40dvw',
        fontFamily: 'Trebuchet MS',
        color: 'rgba(255,255,255,0.7)',
      }}>
        <div style={{
          flex: 'none',
          padding: '0.6dvw 0.9dvw 0.4dvw',
          borderBottom: '0.05dvw solid rgba(255,255,255,0.1)',
        }}>
          <div style={{ fontSize: TITLE_SIZE, color: 'rgba(255,255,255,0.9)' }}>{title}</div>
          <div style={{ fontSize: TEXT_SIZE, marginTop: '0.15dvw' }}>{summary}</div>
          {warning && (
            <div style={{ fontSize: TEXT_SIZE, marginTop: '0.25dvw', color: 'rgb(230, 140, 140)' }}>
              {warning}
            </div>
          )}
        </div>

        {/* Row list — takes the space the header and buttons don't need, and
            scrolls beyond that, so a chart with many unsupported chords stays
            fully reachable without the dialog growing off screen.
            minHeight: 0 is required for a flex child to be allowed to shrink
            below its content height and actually scroll. */}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '0.3dvw 0' }}>
          {rows.length === 0 && (
            <div style={{ padding: '0.5dvw 0.9dvw', fontSize: '0.8dvw' }}>
              No changes to make.
            </div>
          )}
          {rows.map(item => {
            const resolution = item.resolution;

            // Chords with no substitute: listed so the count in the summary is
            // accounted for, but not selectable. Faded, all but the Edit button,
            // which takes the chord to the chart to be edited by hand.
            if (resolution.kind === 'fallback') {
              return (
                <div
                  key={item.index}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5dvw',
                    padding: '0.3dvw 0.9dvw',
                    fontSize: TEXT_SIZE,
                    borderBottom: '0.05dvw solid rgba(255,255,255,0.05)',
                  }}
                >
                  {/* Empty slot keeps the chords aligned with the checkbox rows. */}
                  <span style={{
                    display: 'inline-block',
                    width: SELECT_SLOT,
                    minWidth: SELECT_SLOT,
                    flex: 'none',
                  }} />
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5dvw', opacity: FALLBACK_OPACITY }}>
                    <span style={chordStyle}>{renderChord(resolution.segment)}</span>
                    {/* Same arrow as the substitute rows, so both row types read
                        as "this chord → that outcome" down one column. */}
                    <span style={arrowStyle}>&rarr;</span>
                    <span style={{ fontStyle: 'italic', fontSize: TEXT_SIZE }}>
                      none found
                    </span>
                  </span>
                  {/* data-actionable keeps the app's global click handler from
                      dropping the selection this makes. */}
                  <button
                    data-actionable="true"
                    title="Edit this chord in the chart"
                    onClick={() => onEdit(item.index)}
                    style={{ ...buttonStyle(true), padding: '0.1dvw 0.5dvw' }}
                  >
                    Edit
                  </button>
                </div>
              );
            }

            if (resolution.kind !== 'changed') return null;
            const isRejected = rejected.indexOf(item.index) >= 0;
            return (
              <div
                key={item.index}
                onClick={() => toggle(item.index)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5dvw',
                  padding: '0.3dvw 0.9dvw',
                  fontSize: TEXT_SIZE,
                  cursor: 'pointer',
                  opacity: isRejected ? 0.35 : 1,
                  borderBottom: '0.05dvw solid rgba(255,255,255,0.05)',
                }}
              >
                <input
                  type="checkbox"
                  readOnly
                  checked={!isRejected}
                  style={checkboxStyle}
                />
                <span style={{
                  ...chordStyle,
                  textDecoration: isRejected ? 'line-through' : 'none',
                }}>
                  {renderChord(resolution.fromSegment)}
                </span>
                <span style={arrowStyle}>&rarr;</span>
                <span style={{
                  ...chordStyle,
                  color: isRejected ? 'inherit' : 'rgba(255,255,255,0.95)',
                }}>
                  {renderChord(resolution.toSegment)}
                </span>
              </div>
            );
          })}
        </div>

        <div style={{
          flex: 'none',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '0.5dvw',
          padding: '0.5dvw 0.9dvw',
          borderTop: '0.05dvw solid rgba(255,255,255,0.1)',
        }}>
          <button onClick={onKeepAll} style={buttonStyle(true)}>
            {keepLabel}
          </button>
          <button
            onClick={() => onApply(accepted)}
            disabled={accepted.length === 0}
            style={buttonStyle(accepted.length > 0)}
          >
            {applyLabel}
          </button>
        </div>
      </div>
    </>
  );
};

export default ChordBatchReview;
